/**
 * Shared admin UI building blocks — page header, stat cards, badges,
 * section cards, loading / error / empty states, and a small hook for
 * loading data from an admin callable.
 */
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Center,
  CopyButton,
  Group,
  Loader,
  Progress,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconBrandAndroid,
  IconBrandApple,
  IconCheck,
  IconCopy,
  IconDeviceMobile,
  IconHelpCircle,
  IconRefresh,
  IconWorld,
} from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import type { Channel, Ms, PlatformKey } from '@/lib/adminTypes';
import {
  CHANNEL_COLOR,
  CHANNEL_LABEL,
  PLATFORM_COLOR,
  PLATFORM_LABEL,
  STATUS_COLOR,
  STATUS_LABEL,
  TIER_COLOR,
  TIER_LABEL,
  fmtBytes,
  fmtDateTime,
  fmtRelative,
  label,
} from '@/lib/format';

// ── Data loading ───────────────────────────────────────────────────

export function useAdminData<T>(load: () => Promise<{ data: T }>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const loadRef = useRef(load);
  loadRef.current = load;

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    loadRef
      .current()
      .then((res) => setData(res.data))
      .catch((err: Error) => setError(err.message || 'Something went wrong.'))
      .finally(() => setLoading(false));
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(reload, deps);
  return { data, error, loading, reload };
}

// ── Page chrome ────────────────────────────────────────────────────

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <Group justify="space-between" align="flex-end" wrap="wrap" gap="sm" mb="lg">
      <Stack gap={4}>
        <Title order={2} fw={700} style={{ letterSpacing: -0.3 }}>
          {title}
        </Title>
        {subtitle ? (
          <Text c="dimmed" size="sm">
            {subtitle}
          </Text>
        ) : null}
      </Stack>
      {actions ? <Group gap="xs">{actions}</Group> : null}
    </Group>
  );
}

export function RefreshButton({ onClick, loading }: { onClick: () => void; loading: boolean }) {
  return (
    <Button
      variant="default"
      size="xs"
      leftSection={<IconRefresh size={14} />}
      onClick={onClick}
      loading={loading}
    >
      Refresh
    </Button>
  );
}

export function SectionCard({
  title,
  subtitle,
  action,
  children,
  p = 'lg',
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  p?: string | number;
}) {
  return (
    <Card withBorder radius="lg" p={p} h="100%">
      {title ? (
        <Group justify="space-between" align="flex-start" mb="md" wrap="nowrap" gap="xs">
          <Stack gap={2}>
            <Text fw={650} size="md">
              {title}
            </Text>
            {subtitle ? (
              <Text c="dimmed" size="xs">
                {subtitle}
              </Text>
            ) : null}
          </Stack>
          {action}
        </Group>
      ) : null}
      {children}
    </Card>
  );
}

export function StatCard({
  label: statLabel,
  value,
  hint,
  icon,
  color = 'brand',
  onClick,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  icon: ReactNode;
  color?: string;
  onClick?: () => void;
}) {
  const body = (
    <Card withBorder radius="lg" p="lg" h="100%">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={6}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: 0.4 }}>
            {statLabel}
          </Text>
          <Text fw={750} style={{ fontSize: 28, lineHeight: 1.1, letterSpacing: -0.5 }}>
            {value}
          </Text>
          {hint ? (
            <Text size="xs" c="dimmed">
              {hint}
            </Text>
          ) : null}
        </Stack>
        <ThemeIcon variant="light" color={color} size={40} radius="md">
          {icon}
        </ThemeIcon>
      </Group>
    </Card>
  );
  return onClick ? (
    <UnstyledButton onClick={onClick} style={{ display: 'block', height: '100%' }}>
      {body}
    </UnstyledButton>
  ) : (
    body
  );
}

// ── States ─────────────────────────────────────────────────────────

export function LoadingBlock({ label: text = 'Loading…' }: { label?: string }) {
  return (
    <Center py={80}>
      <Stack align="center" gap="xs">
        <Loader />
        <Text c="dimmed" size="sm">
          {text}
        </Text>
      </Stack>
    </Center>
  );
}

export function ErrorBlock({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <Alert color="red" radius="lg" icon={<IconAlertTriangle size={18} />} title="Couldn't load this page">
      <Stack gap="xs">
        <Text size="sm">{message}</Text>
        {onRetry ? (
          <Button size="xs" variant="light" color="red" w="fit-content" onClick={onRetry}>
            Try again
          </Button>
        ) : null}
      </Stack>
    </Alert>
  );
}

export function EmptyState({ title, body }: { title: string; body?: string }) {
  return (
    <Center py="xl">
      <Stack align="center" gap={4}>
        <Text fw={600} size="sm">
          {title}
        </Text>
        {body ? (
          <Text c="dimmed" size="xs" ta="center" maw={360}>
            {body}
          </Text>
        ) : null}
      </Stack>
    </Center>
  );
}

// ── Badges ─────────────────────────────────────────────────────────

export function TierBadge({ tier }: { tier: string }) {
  return (
    <Badge color={TIER_COLOR[tier] ?? 'gray'} variant="light" radius="sm">
      {label(TIER_LABEL, tier)}
    </Badge>
  );
}

export function StatusBadge({ status }: { status: string }) {
  return (
    <Badge color={STATUS_COLOR[status] ?? 'gray'} variant="dot" radius="sm">
      {label(STATUS_LABEL, status)}
    </Badge>
  );
}

export function ChannelBadge({ channel, sandbox }: { channel: Channel; sandbox?: boolean }) {
  return (
    <Group gap={4} wrap="nowrap">
      <Badge color={CHANNEL_COLOR[channel]} variant={channel === 'free' ? 'outline' : 'light'} radius="sm">
        {CHANNEL_LABEL[channel]}
      </Badge>
      {sandbox ? (
        <Tooltip label="Test purchase (sandbox) — not real revenue">
          <Badge color="yellow" variant="light" radius="sm">
            Test
          </Badge>
        </Tooltip>
      ) : null}
    </Group>
  );
}

const PLATFORM_ICON: Record<PlatformKey, ReactNode> = {
  ios: <IconBrandApple size={12} />,
  android: <IconBrandAndroid size={12} />,
  web: <IconWorld size={12} />,
  mobile: <IconDeviceMobile size={12} />,
  unknown: <IconHelpCircle size={12} />,
};

export function PlatformBadge({ platform, suffix }: { platform: PlatformKey; suffix?: string }) {
  return (
    <Badge
      color={PLATFORM_COLOR[platform]}
      variant={platform === 'unknown' ? 'outline' : 'light'}
      radius="sm"
      leftSection={PLATFORM_ICON[platform]}
      tt="none"
    >
      {PLATFORM_LABEL[platform]}
      {suffix ? ` ${suffix}` : ''}
    </Badge>
  );
}

// ── Small pieces ───────────────────────────────────────────────────

export function RelTime({ ms, prefix }: { ms: Ms; prefix?: string }) {
  if (!ms) return <Text span c="dimmed" size="sm">—</Text>;
  return (
    <Tooltip label={fmtDateTime(ms)} withArrow>
      <Text span size="sm">
        {prefix ? `${prefix} ` : ''}
        {fmtRelative(ms)}
      </Text>
    </Tooltip>
  );
}

export function StudioAvatar({ name, logoUrl, size = 36 }: { name: string; logoUrl: string | null; size?: number }) {
  const initials = name
    .replace(/[^A-Za-z0-9 ]+/g, ' ')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <Avatar src={logoUrl} size={size} radius="md" color="brand" variant="light">
      {initials || '?'}
    </Avatar>
  );
}

export function CopyText({ value, children }: { value: string; children?: ReactNode }) {
  return (
    <Group gap={4} wrap="nowrap">
      {children ?? (
        <Text size="sm" ff="monospace">
          {value}
        </Text>
      )}
      <CopyButton value={value} timeout={1500}>
        {({ copied, copy }) => (
          <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow>
            <UnstyledButton onClick={copy} c={copied ? 'teal' : 'dimmed'} style={{ display: 'flex' }}>
              {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
            </UnstyledButton>
          </Tooltip>
        )}
      </CopyButton>
    </Group>
  );
}

/** Horizontal share bars for a small categorical breakdown. */
export function MixBars({
  data,
  labels,
  colors,
  order,
}: {
  data: Record<string, number>;
  labels: Record<string, string>;
  colors: Record<string, string>;
  order?: string[];
}) {
  const keys = (order ?? Object.keys(data)).filter((k) => (data[k] ?? 0) > 0);
  const total = keys.reduce((n, k) => n + (data[k] ?? 0), 0);
  if (total === 0) return <EmptyState title="No data yet" />;
  return (
    <Stack gap="sm">
      {keys.map((k) => {
        const n = data[k] ?? 0;
        const pct = Math.round((n / total) * 100);
        return (
          <Box key={k}>
            <Group justify="space-between" mb={4}>
              <Text size="sm">{labels[k] ?? k}</Text>
              <Text size="sm" fw={600}>
                {n}
                <Text span c="dimmed" size="xs" fw={400}>
                  {'  '}
                  {pct}%
                </Text>
              </Text>
            </Group>
            <Progress value={pct} color={colors[k] ?? 'gray'} size="sm" radius="xl" />
          </Box>
        );
      })}
    </Stack>
  );
}

/** Storage used vs the plan limit — colour turns amber at 80%, red at 100%. */
export function StorageBar({
  used,
  limit,
  compact,
}: {
  used: number;
  limit: number | null;
  compact?: boolean;
}) {
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const color = !limit ? 'brand' : pct >= 100 ? 'red' : pct >= 80 ? 'orange' : 'brand';
  return (
    <Stack gap={compact ? 2 : 4} w="100%">
      <Group justify="space-between" gap={4} wrap="nowrap">
        <Text size={compact ? 'xs' : 'sm'} fw={compact ? 500 : 600}>
          {fmtBytes(used)}
          <Text span c="dimmed" size="xs" fw={400}>
            {' '}
            / {fmtBytes(limit)}
          </Text>
        </Text>
        {limit ? (
          <Text size="xs" c={pct >= 80 ? color : 'dimmed'} fw={pct >= 80 ? 600 : 400}>
            {pct}%
          </Text>
        ) : null}
      </Group>
      <Progress value={limit ? Math.max(pct, used > 0 ? 1 : 0) : 0} color={color} size={compact ? 'xs' : 'sm'} radius="xl" />
    </Stack>
  );
}
